<script setup lang="ts">
import type { IPage, PageNames } from '@/interfaces/IPage'
import { depositNeedOtpCurrency } from '@nami/domain/classes'
import { computed, ref, toRefs, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import RedPoint from '@/components/base/RedPoint.vue'
import router from '@/router'
import { useGlobalStore } from '@/stores/globalStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { useUserStore } from '@/stores/userStore'

const route = useRoute()
const currentTab = ref<IPage>('' as IPage)
const isSelectedTab = (tabName: (string | undefined)[]): boolean => {
    return tabName.includes(currentTab.value)
}
const globalStore = useGlobalStore()
const userStore = useUserStore()
const { userInfo } = toRefs(userStore)
const notificationStore = useNotificationStore()
notificationStore.fetch()

const isHistoryUnread = computed(() => notificationStore.isHistoryUnread)
const mainToggleHistoryRedPoint = computed(() => globalStore.mainToggle.mainToggleHistoryRedPoint)

const pageNames: PageNames = {
    'deposit': 'deposit',
    'withdraw': 'withdraw',
    'withdraw-processing': 'withdraw-processing',
    'withdraw-reach-limit': 'withdraw-reach-limit',
    'promotion-pending-deposit': 'promotion-pending-deposit',
    'promotion-progress': 'promotion-progress',
    'kyc-pending': 'kyc-pending',
    'history': 'history',
    'setting': 'setting',
    'Deposit': 'Deposit',
    'cpf': 'cpf',
}

const getRouterPath = (tabName: string): string => {
    return pageNames[tabName] ?? ''
}

const switchTab = (tabName: string) => {
    if (tabName === 'withdraw') {
        window.location.href
            = `${window.location.origin}/payment/withdraw?currency=${userInfo.value.currency.toString()}`
        // router.push(`/payment/withdraw?currency=${currency}`); unmark it if no need to use hector toggle NewPaymentWithdrawPageToggle to control
    }
    else {
        if (tabName === pageNames.history) {
            localStorage.setItem('historyTrxType', 'all')
        }
        // router.push(`/payment/${getRouterPath(tabName)}`);
        window.location.href = `${window.location.origin}/payment/${getRouterPath(tabName)}`
    }
}

// by default go to deposit page
if (router.currentRoute.value.fullPath === '/dist/') {
    window.location.href = `${window.location.origin}/payment/deposit`
    // router.push('/payment/deposit'); waiting for add bank feature open
}

const getCurrentTabName = (): void => {
    const pageName = route.path.includes('cpf') ? 'cpf' : route.path.split('/')[2]
    const entries = Object.entries(pageNames)

    for (const [key, value] of entries) {
        if (pageName === value) {
            currentTab.value = key as IPage
        }
    }
}

const prevPageNameIs = (pageName: string) => {
    const fromPageName = localStorage.getItem('fromPageName')
    if (fromPageName === pageName) {
        return 'setting'
    }
    const displayTab = depositNeedOtpCurrency.includes(userInfo.value.currency.code) ? 'deposit' : 'withdraw'
    if (!fromPageName && pageName === displayTab) {
        return 'setting'
    }
}

const tabMapping = {
    deposit: ['deposit', 'Deposit', 'cpf', 'kyc-pending', prevPageNameIs('deposit')],
    withdraw: [
        'withdraw',
        'withdraw-processing',
        'withdraw-reach-limit',
        'promotion-pending-deposit',
        'promotion-progress',
        // 'kyc-pending',
        prevPageNameIs('withdraw'),
    ],
    history: ['history'],
}

watchEffect(() => {
    getCurrentTabName()
})
</script>

<template>
    <nav class="nav">
        <ul class="nav-list">
            <li
                class="nav-item"
                data-gtm-category="Deposit"
                data-gtm-label="Deposit_Tab_Click"
                :class="{ selected: isSelectedTab(tabMapping.deposit) }"
                @click="switchTab('deposit')"
            >
                <div class="content">
                    <div class="link">
                        {{ $t('deposit') }}
                    </div>
                    <div class="selected-line" />
                </div>
            </li>
            <li
                class="nav-item"
                data-gtm-category="Withdraw"
                data-gtm-label="Withdraw_Tab_Click"
                :class="{ selected: isSelectedTab(tabMapping.withdraw) }"
                @click="switchTab('withdraw')"
            >
                <div class="content">
                    <div class="link">
                        {{ $t('withdraw') }}
                    </div>
                    <div class="selected-line" />
                </div>
            </li>
            <li
                class="nav-item"
                data-gtm-category="History"
                data-gtm-label="History_Tab_Click"
                :class="{ selected: isSelectedTab(tabMapping.history) }"
                @click="switchTab('history')"
            >
                <div class="content">
                    <div class="link">
                        {{ $t('history') }}
                    </div>
                    <RedPoint
                        v-if="mainToggleHistoryRedPoint && isHistoryUnread && !isSelectedTab(tabMapping.history)"
                        class="history-tab-red-point"
                    />
                    <div class="selected-line" />
                </div>
            </li>
        </ul>
    </nav>
</template>
